import React from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { Text } from "ui/Typography"
import IconWithBg from "ui/IconWithBg"
import moment from "moment"
import EventBlock from "../EventBlock"
import { getJournalIcon } from "../../../utils"
import { getEventColor, getEventBorderColor, overlaps } from "pagesV2/pro/Calendar/utils"
import { getEventPosition, HOUR_HEIGHT } from "../timeGridUtils"
import styles from "../index.module.css"

const AgendaWeekView = ({ calendarDays, hours, totalHeight, startHour, gridTemplateColumns, gridMinWidth, eventsByDayWithCols, timeOffByDay, journalNoteDates, isCompact, active, canViewJournal, canCreateJournal, canEditJournal, setIsBookingModalOpen, setSelectedBookingId, onJournalOpen }) => (
  <div className={styles.timeGridRoot}>
    <div className={`${styles.timeGridTable} ${styles.weekGrid}`} style={{ gridTemplateRows: `80px repeat(${hours.length}, ${HOUR_HEIGHT}px)`, gridTemplateColumns, minWidth: `${gridMinWidth}px` }}>
      {/* Header: empty time corner */}
      <div className={styles.timeGridHeaderCell} style={{ gridRow: 1, gridColumn: 1 }} />

      {/* Day header cells */}
      {calendarDays.map((day, dayIdx) => {
        const dayStr = day.format("YYYY-MM-DD")
        const dayLabel = isCompact ? day.format("dd")[0] : day.format("ddd")
        const hasJournalNote = journalNoteDates?.includes(dayStr)
        const hasJournalPermission = hasJournalNote ? canViewJournal || canEditJournal : canCreateJournal
        return (
          <Flex key={dayStr} gap={5} className={`py-s px-xs ${styles.timeGridHeaderCell}`} align="center" justify="center" style={{ gridRow: 1, gridColumn: dayIdx + 2 }}>
            <Flex vertical justify={isCompact ? "space-between" : "center"} className={`${isCompact ? "h-full" : ""} ml-auto`}>
              <Text fw="500">{dayLabel}</Text>
              <Text fw="500">{day.format("D")}</Text>
            </Flex>
            {!isCompact && hasJournalPermission && (
              <Flex className="ml-auto" align="center">
                <IconWithBg disabled={!active} icon={getJournalIcon(journalNoteDates, dayStr)} onClick={active ? () => onJournalOpen(dayStr) : undefined} />
              </Flex>
            )}
          </Flex>
        )
      })}

      {/* Time labels */}
      {hours.map((hour, hIdx) => (
        <div key={`label-${hour}`} className={styles.timeLabelCell} style={{ gridRow: hIdx + 2, gridColumn: 1 }}>
          <Text fw="500" className="py-m">{moment(hour, "H").format("h A")}</Text>
        </div>
      ))}

      {/* Day columns */}
      {calendarDays.map((day, dayIdx) => {
        const dayStr = day.format("YYYY-MM-DD")
        const eventsWithCols = eventsByDayWithCols[dayStr] || []
        const timeOffForDay = timeOffByDay[dayStr]
        return (
          <div key={`col-${dayStr}`} className={styles.dayColumn} style={{ gridRow: `2 / span ${hours.length}`, gridColumn: dayIdx + 2, height: totalHeight }}>
            {hours.map((hour, hIdx) => (
              <div key={`divider-${hour}`} className={styles.hourDivider} style={{ top: hIdx * HOUR_HEIGHT }} />
            ))}
            {timeOffForDay?.allDay && <div className={`${styles.timeOffOverlay} ${styles.timeOffOverlayFull}`} />}
            {!timeOffForDay?.allDay && (timeOffForDay?.ranges || []).map((toff) => {
              const topPx = Math.max(0, (toff.start.hour() - startHour) * 60 + toff.start.minute()) * (HOUR_HEIGHT / 60)
              const botPx = Math.max(0, (toff.end.hour() - startHour) * 60 + toff.end.minute()) * (HOUR_HEIGHT / 60)
              return <div key={`${dayStr}-${toff.start.format("HH:mm")}`} className={styles.timeOffOverlay} style={{ top: topPx, height: Math.max(0, botPx - topPx) }} />
            })}
            {eventsWithCols.map((ev) => {
              const pos = getEventPosition(ev, startHour)
              if (!pos) return null
              const colW = 100 / ev.totalCols
              const conflict = timeOffForDay?.allDay || (timeOffForDay?.ranges || []).some((r) => overlaps(ev.startMoment, ev.endMoment, r.start, r.end))
              return (
                <div key={ev.id ?? ev.title + ev.start} className={styles.eventBlockPositioned} style={{ top: pos.top, height: pos.height, left: `${ev.colIndex * colW}%`, width: `${colW}%` }}>
                  <EventBlock event={ev} eventColor={getEventColor(ev.color)} eventBorderColor={getEventBorderColor()} conflict={conflict} setIsBookingModalOpen={setIsBookingModalOpen} setSelectedBookingId={setSelectedBookingId} fullHeight />
                </div>
              )
            })}
          </div>
        )
      })}
    </div>
  </div>
)

AgendaWeekView.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  hours: PropTypes.array.isRequired,
  totalHeight: PropTypes.number.isRequired,
  startHour: PropTypes.number.isRequired,
  gridTemplateColumns: PropTypes.string.isRequired,
  gridMinWidth: PropTypes.number.isRequired,
  normalizedEventsByDay: PropTypes.object.isRequired,
  eventsByDayWithCols: PropTypes.object.isRequired,
  timeOffByDay: PropTypes.object.isRequired,
  journalNoteDates: PropTypes.array,
  isCompact: PropTypes.bool,
  active: PropTypes.bool,
  canViewJournal: PropTypes.bool,
  canCreateJournal: PropTypes.bool,
  canEditJournal: PropTypes.bool,
  setIsBookingModalOpen: PropTypes.func.isRequired,
  setSelectedBookingId: PropTypes.func.isRequired,
  onJournalOpen: PropTypes.func.isRequired,
}

export default AgendaWeekView
